import { useState, useEffect } from 'react';
import { useMappedInSettings } from './useMappedInSettings';

const useMappedInLocations = (userId, mappedInConfig, configLoading) => {
    const [locations, setLocations] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);

    // Fetch MappedIn settings


    // MappedIn configuration - use from settings or fallback to default
    const MAPPEDIN_OPTIONS = mappedInConfig

    // Load MappedIn SDK and fetch locations and categories
    const loadMappedInAndFetchData = async () => {
        if (configLoading || !mappedInConfig) return;

        try {
            setLoading(true);
            setError(null);



            console.log('🔄 Loading MappedIn SDK...');

            // Try to import MappedIn SDK
            const mappedInModule = await import('@mappedin/mappedin-js');
            const { getMapData } = mappedInModule;

            console.log('✅ MappedIn SDK loaded successfully');

            console.log('🔄 Fetching MappedIn data with options:', MAPPEDIN_OPTIONS);
            const mapData = await getMapData(MAPPEDIN_OPTIONS);

            console.log('✅ Map data received:', mapData);

            // Fetch Categories
            console.log('🔄 Fetching categories...');
            const enterpriseCategories = mapData.getByType('enterprise-category');

            console.log('📂 Raw enterprise categories:', enterpriseCategories);
            console.log('📂 Number of enterprise categories:', enterpriseCategories?.length || 0);

            let formattedCategories = [];
            if (enterpriseCategories && enterpriseCategories.length > 0) {
                formattedCategories = enterpriseCategories
                    .filter(category => {
                        const hasName = category.name && category.name.trim() !== '';
                        if (!hasName) {
                            console.log('🚫 Filtering out category without name:', category);
                        }
                        return hasName;
                    })
                    .map(category => ({
                        id: category.id,
                        name: category.name.trim(),
                        description: category.description || '',
                        floor: category.floor?.name || category.floor?.id || '',
                        externalId: category.externalId || '',
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));

                console.log(`✅ Formatted ${formattedCategories.length} categories:`, formattedCategories);
            } else {
                console.warn('⚠️ No enterprise categories found');
            }

            // Fetch Locations
            console.log('🔄 Fetching locations...');
            const enterpriseLocations = mapData.getByType('enterprise-location');
            console.log('📍 Raw enterprise locations:', enterpriseLocations);
            console.log('📍 Number of enterprise locations:', enterpriseLocations?.length || 0);

            let formattedLocations = [];
            if (!enterpriseLocations || enterpriseLocations.length === 0) {
                console.warn('⚠️ No enterprise locations found, falling back to spaces');
                // Fallback to spaces if no enterprise locations
                const spacesData = mapData.getByType('space');

                if (!spacesData || spacesData.length === 0) {
                    console.warn('⚠️ No spaces found either');
                    if (formattedCategories.length === 0) {
                        setError('No locations or categories found in the MappedIn map. Please check the map configuration.');
                        return;
                    }
                } else {
                    // Format spaces data
                    formattedLocations = spacesData
                        .filter(space => space.name && space.name.trim() !== '')
                        .map(space => ({
                            id: space.id,
                            name: space.name.trim(),
                            description: space.description || '',
                            floor: space.floor?.name || space.floor?.id || '',
                            externalId: '', // Spaces don't have externalId
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name));

                    console.log(`✅ Formatted ${formattedLocations.length} locations (fallback):`, formattedLocations);
                }
            } else {
                // Format enterprise locations
                formattedLocations = enterpriseLocations
                    .filter(location => {
                        const hasName = location.name && location.name.trim() !== '';
                        if (!hasName) {
                            console.log('🚫 Filtering out location without name:', location);
                        }
                        return hasName;
                    })
                    .map(location => ({
                        id: location.id,
                        name: location.name.trim(),
                        description: location.description || '',
                        floor: location.floor?.name || location.floor?.id || '',
                        externalId: location.externalId || '', // Store externalId
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));

                console.log(`✅ Formatted ${formattedLocations.length} locations:`, formattedLocations);
            }

            if (formattedLocations.length === 0 && formattedCategories.length === 0) {
                setError('No valid locations or categories found. All items may be missing names.');
                return;
            }

            setLocations(formattedLocations);
            setCategories(formattedCategories);
            setInitialized(true);
        } catch (err) {
            console.error('❌ Error loading MappedIn or fetching data:', err);

            // More specific error messages
            let errorMessage = 'Failed to load MappedIn data. ';
            if (err.message.includes('401') || err.message.includes('Unauthorized')) {
                errorMessage += 'Invalid API credentials.';
            } else if (err.message.includes('404') || err.message.includes('Not Found')) {
                errorMessage += 'Map not found. Check the mapId.';
            } else if (err.message.includes('network') || err.message.includes('fetch')) {
                errorMessage += 'Network error. Check your internet connection.';
            } else {
                errorMessage += err.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Initialize on component mount or when config changes
    useEffect(() => {
        if (!configLoading && mappedInConfig) {
            console.log('🚀 useMappedInLocations initializing...');
            loadMappedInAndFetchData();
        }
    }, [configLoading, mappedInConfig]);

    return { locations, categories, loading, error, loadMappedInAndFetchData, setInitialized };
};

export default useMappedInLocations;

